/**
 * PulseActivityCard — Activity summary card for the Pulse Home Dashboard.
 *
 * Renders a `DashboardCardShell` containing:
 *  - A `SegmentedBarChart` of the bucketed data (stacked Solved/Pending/Urgent)
 *  - A subtitle showing the grand total
 *  - Three stat pills: Solved (green), Pending (yellow), Urgent (red)
 *
 * "View all" navigates to /activities.
 *
 * Consumes `useHomeActivitySummary(range)` — the root `homeActivitySummary`
 * query (the same query used by the OEM ActivityCard; no new backend code needed).
 * Loading / empty / error states delegate to the `DashboardCardShell` slots
 * using the Crop Circle feedback primitives (Skeleton / EmptyState / ErrorState).
 * Text cells use Crop Circle tokens Text — never bare Paper Text.
 *
 * Design matches the gripp-os PulseHomeMapView Activity card:
 *  - SegmentedBarChart fills the flex-1 area
 *  - Solved / Pending / Urgent stat pills sit beneath it
 *  - Pressing the header selects the card (replots map in Phase 2 wiring)
 *  - Pressing a stat pill selects that activity status
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { SegmentedBarChart } from './segmentedBarChart';
import { useHomeActivitySummary } from '../hooks/useHomeActivitySummary';
import { HomeCardProps } from '../types';

/**
 * Single stat — colored dot + dark count + soft label (design: PulseHomeMapView
 * StatPill). Pressing one filters the map to that activity status.
 */
interface StatPillProps {
  color: string;
  count: number;
  label: string;
  /** Whether this pill's status currently filters the map. */
  active?: boolean;
  /** Whether some OTHER pill is active (dim this one). */
  dimmed?: boolean;
  onPress?: () => void;
  testID?: string;
}

const StatPill: React.FC<StatPillProps> = ({
  color,
  count,
  label,
  active,
  dimmed,
  onPress,
  testID,
}) => (
  <Pressable
    style={[
      styles.statPill,
      active && styles.statPillActive,
      dimmed && styles.statPillDimmed,
    ]}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    testID={testID}
  >
    <View style={[styles.statDot, { backgroundColor: color }]} />
    <Text style={styles.statCount}>{count}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </Pressable>
);

export const PulseActivityCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeActivitySummary(range);

  const handleViewAll = () => {
    navigate('/activities');
  };

  const totalLabel = data
    ? t('home.activity.total', '{{count}} total', { count: data.total })
    : undefined;

  const action = (
    <ViewAllLink
      label={t('home.activity.viewAll', 'View all')}
      onPress={handleViewAll}
      testID="pulse-activity-card-view-all"
    />
  );

  // Loading state
  if (isLoading) {
    return (
      <DashboardCardShell
        title={t('home.activity.title', 'Activity')}
        action={action}
        testID="pulse-activity-card"
        loadingSlot={
          <View style={styles.loadingBody}>
            <Skeleton height={80} radius={4} style={styles.chartSkeleton} />
            <View style={styles.pillRow}>
              <Skeleton width={64} height={28} radius={tokens.borderRadius.full} />
              <Skeleton width={64} height={28} radius={tokens.borderRadius.full} />
              <Skeleton width={64} height={28} radius={tokens.borderRadius.full} />
            </View>
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardCardShell
        title={t('home.activity.title', 'Activity')}
        action={action}
        testID="pulse-activity-card"
        errorSlot={
          <ErrorState
            title={t('home.activity.error', 'Could not load activity data')}
            testID="pulse-activity-card-error"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  // Empty state
  if (!data || data.total === 0) {
    return (
      <DashboardCardShell
        title={t('home.activity.title', 'Activity')}
        action={action}
        testID="pulse-activity-card"
        emptySlot={
          <EmptyState
            title={t('home.activity.empty', 'No activity for this period')}
            testID="pulse-activity-card-empty"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={t('home.activity.title', 'Activity')}
      subtitle={totalLabel}
      action={action}
      testID="pulse-activity-card"
      allowOverflow
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.activity.plot', 'Plot activities on the map')}
    >
      <View style={styles.cardBody}>
        {/* Stacked bar chart — fills the body; pills sit beneath it. */}
        <View style={styles.chartArea} testID="pulse-activity-card-chart">
          <SegmentedBarChart data={data.buckets} animKey={range} />
        </View>

        {/* Stat pills: Solved · Pending · Urgent. Pressing one filters the map
            to that status (the value matches the activity point's bucket). */}
        <View style={styles.pillRow}>
          <StatPill
            color={tokens.colors.semantic.success}
            count={data.solved}
            label={t('home.activity.solved', 'Solved')}
            active={activeStatus === 'solved'}
            dimmed={!!activeStatus && activeStatus !== 'solved'}
            onPress={() => onSelectStatus?.('solved')}
            testID="pulse-activity-card-stat-solved"
          />
          <StatPill
            color={tokens.colors.semantic.warning}
            count={data.pending}
            label={t('home.activity.pending', 'Pending')}
            active={activeStatus === 'pending'}
            dimmed={!!activeStatus && activeStatus !== 'pending'}
            onPress={() => onSelectStatus?.('pending')}
            testID="pulse-activity-card-stat-pending"
          />
          <StatPill
            color={tokens.colors.semantic.error}
            count={data.urgent}
            label={t('home.activity.urgent', 'Urgent')}
            active={activeStatus === 'urgent'}
            dimmed={!!activeStatus && activeStatus !== 'urgent'}
            onPress={() => onSelectStatus?.('urgent')}
            testID="pulse-activity-card-stat-urgent"
          />
        </View>
      </View>
    </DashboardCardShell>
  );
};

PulseActivityCard.displayName = 'PulseActivityCard';

const styles = StyleSheet.create({
  cardBody: {
    flex: 1,
  },
  chartArea: {
    // Grows to fill the card body (design: chart is flex-1); the chart itself
    // fills this when SegmentedBarChart is given no explicit height.
    flex: 1,
    minHeight: 0,
  },
  loadingBody: {
    gap: tokens.spacing.sm,
  },
  chartSkeleton: {
    width: '100%',
  },
  pillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md, // gap-x-3 (12)
    marginTop: tokens.spacing.md, // mt-3 (12)
  },
  statPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6, // design: gap-1.5 (6px) — no exact spacing token (xs=4, sm=8)
    paddingVertical: 2,
    paddingHorizontal: tokens.spacing.xs,
    marginHorizontal: -tokens.spacing.xs,
    borderRadius: tokens.borderRadius.sm,
  },
  statPillActive: {
    backgroundColor: Colors.role.surfaceInverseSoft,
  },
  statPillDimmed: {
    opacity: 0.45,
  },
  statDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  statCount: {
    ...tokens.typography.label.medium,
    // design: text-[13px] leading-[18px] — matches the card title size
    // (between label.medium 12/16 and label.large 14/20, no exact token).
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: Colors.role.textStrong,
    fontVariant: ['tabular-nums'],
  },
  statLabel: {
    ...tokens.typography.label.medium,
    fontWeight: '400',
    color: Colors.role.textSoft,
  },
});
